import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';

import { useAuth } from '../auth/AuthContext';
import GeneralSettings from './settings/GeneralSettings';
import MediaSearchSettings from './settings/MediaSearchSettings';
import TranscodingSettings from './settings/TranscodingSettings';
import UsersPage from './UsersPage';

/// Everything an operator configures, in one place with a submenu: the server's own identity,
/// the people who may use it, what it does when a client cannot play something directly, and
/// what it looks for when it has to fetch a version of something.
export default function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();

  const tabs = [
    { path: '/settings/general', labelKey: 'settings.tab.general', visible: can('ManageServerSettings') },
    { path: '/settings/users', labelKey: 'settings.tab.users', visible: can('ManageUsers') },
    { path: '/settings/transcoding', labelKey: 'settings.tab.transcoding', visible: can('ManageServerSettings') },
    { path: '/settings/media-search', labelKey: 'settings.tab.mediaSearch', visible: can('ManageServerSettings') },
  ].filter((tab) => tab.visible);

  const current = tabs.find((tab) => location.pathname.startsWith(tab.path))?.path ?? tabs[0]?.path;

  if (tabs.length === 0) {
    return <Alert severity="error">{t('auth.forbidden')}</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h2">{t('settings.title')}</Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={current} onChange={(_, value) => navigate(value)} variant="scrollable" allowScrollButtonsMobile>
          {tabs.map((tab) => (
            <Tab key={tab.path} value={tab.path} label={t(tab.labelKey)} />
          ))}
        </Tabs>
      </Box>

      <Routes>
        <Route index element={<Navigate to={current ?? '/settings/general'} replace />} />
        <Route path="general" element={<GeneralSettings />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="transcoding" element={<TranscodingSettings />} />
        <Route path="media-search" element={<MediaSearchSettings />} />
        <Route path="*" element={<Navigate to={current ?? '/settings/general'} replace />} />
      </Routes>
    </Stack>
  );
}
